import { useCallback, useMemo } from 'react';
import { GamepadActionId } from 'generated/graphql';
import {
  buildDictaphoneActionMetric,
  createDictaphoneButtonNameByKeyCode,
  DictaphoneActionMetricPayload,
  getMatchedDictaphoneProduct,
} from './dictaphoneActionMetric';

type BuildDictaphoneActionMetricInput =
  | {
      action: GamepadActionId;
      inputMode: 'gamepad';
      gamepadKeyCode?: string;
    }
  | {
      action: GamepadActionId;
      inputMode: 'webhid';
      webhidValue: number;
    };

type UseDictaphoneActionMetricBuilderParams = {
  audioInputDeviceId: string;
  audioInputDeviceLabel: string;
  isRecording: boolean;
};

export const useDictaphoneActionMetricBuilder = ({
  audioInputDeviceId,
  audioInputDeviceLabel,
  isRecording,
}: UseDictaphoneActionMetricBuilderParams) => {
  const dictaphoneProduct = useMemo(
    () => getMatchedDictaphoneProduct({ deviceLabel: audioInputDeviceLabel, audioInputDeviceId }),
    [audioInputDeviceId, audioInputDeviceLabel]
  );
  const dictaphoneButtons = useMemo(
    () => dictaphoneProduct?.buttons ?? [],
    [dictaphoneProduct?.buttons]
  );
  const dictaphoneButtonNameByKeyCode = useMemo(
    () => createDictaphoneButtonNameByKeyCode(dictaphoneButtons),
    [dictaphoneButtons]
  );

  return useCallback(
    (input: BuildDictaphoneActionMetricInput): DictaphoneActionMetricPayload =>
      buildDictaphoneActionMetric({
        ...input,
        audioInputDeviceLabel,
        dictaphoneProductId: dictaphoneProduct?.id,
        dictaphoneButtonNameByKeyCode,
        isRecording,
      }),
    [audioInputDeviceLabel, dictaphoneProduct?.id, dictaphoneButtonNameByKeyCode, isRecording]
  );
};
